import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui';
import { Lesson } from '@court-sync/shared';
import { useFormatDate } from '../../hooks/useFormatDate';

export interface LowEnrollmentOccurrence {
  date: string;
  enrollment: number;
  minimumRequired: number;
}

export interface LowEnrollmentLessonAlert {
  lesson: Lesson;
  occurrences: LowEnrollmentOccurrence[];
}

interface LowEnrollmentAlertProps {
  alerts: LowEnrollmentLessonAlert[];
  isLoading?: boolean;
  onViewLesson: (lessonId: string) => void;
  onCancelTerm?: (lessonId: string) => void;
}

export const LowEnrollmentAlert: React.FC<LowEnrollmentAlertProps> = ({
  alerts,
  isLoading = false,
  onViewLesson,
  onCancelTerm,
}) => {
  const { formatDate } = useFormatDate();

  // Don't show anything while loading or when there are no alerts (avoids flicker)
  if (isLoading || alerts.length === 0) {
    return null;
  }

  return (
    <div className="bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800/80 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-3">
            Low Enrollment Alert
          </h3>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.lesson.id}
                className="bg-white dark:bg-gray-950/80 rounded p-3 border border-orange-100 dark:border-orange-800/60"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {alert.lesson.name}
                    </p>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {alert.occurrences.map((occ) => (
                        <p key={occ.date}>
                          {occ.enrollment} of {occ.minimumRequired} minimum students
                          (starts {formatDate(occ.date)})
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewLesson(alert.lesson.id)}
                    >
                      View
                    </Button>
                    {onCancelTerm && alert.lesson.lessonType === 'group' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onCancelTerm(alert.lesson.id)}
                      >
                        Cancel Term
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LowEnrollmentAlert;
